import { Injectable, NgZone } from '@angular/core';
import { Toast, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, interval, Subject, Subscription } from 'rxjs';
import { delay, takeWhile } from 'rxjs/operators';

export interface VoiceCommand {
  command: string;
  text?: string;
  targetField?: string;
}

export interface VoiceWritingState {
  isListening: boolean;
  isActive: boolean;
  isDictating: boolean;
  activeElement: HTMLElement | null;
  lastCommand: string | null;
  error: string | null;
  processingCommand: boolean;
  currentField: string | null;
  uppercaseMode: boolean;
  networkStatus: 'online' | 'offline' | 'recovering'; // Estado de red
  isRecovering: boolean; // Indicador de recuperación
}

@Injectable({ providedIn: 'root' })
export class VoiceWriting2Service {
  private recognition: any;
  private isSupported = false;
  private activeElement: HTMLInputElement | HTMLTextAreaElement | null = null;
  private recognitionTimeout: any;
  private commandProcessingTimeout: any;
  private restartAttempts = 0;
  private maxRestartAttempts = 3;
  private startCommandDetected = false;

  //  Variables para manejo de red
  private networkCheckSubscription: Subscription | null = null;
  private networkCheckInterval = 3000; // Revisar cada 3 segundos
  private maxNetworkRetries = 10; // Máximo 30 segundos de reintento
  private currentNetworkRetries = 0;

  private intervalId: any;
 private iterationCount = 0;
  private readonly MAX_ITERATIONS = 8;

  // Estado del servicio
  private stateSubject = new BehaviorSubject<VoiceWritingState>({
    isListening: false,
    isActive: false,
    isDictating: false,
    activeElement: null,
    lastCommand: null,
    error: null,
    processingCommand: false,
    currentField: null,
    uppercaseMode: false,
    networkStatus: 'online', //
    isRecovering: false //
    });

  // Observables públicos
  public state$ = this.stateSubject.asObservable();
  private uiCommandSubject = new Subject<{ type: string; payload?: any; voiceCommand?: VoiceCommand }>();
  public uiCommands$ = this.uiCommandSubject.asObservable();
  public commandExecuted$ = new Subject<VoiceCommand>();

  // Configuración de comandos de control
  private controlCommands: { [key: string]: string } = {
    'texto': 'START_DICTATION',
    'empezar texto': 'START_DICTATION',
    'iniciar texto': 'START_DICTATION',
    'dictar': 'START_DICTATION',
    'empezar dictado': 'START_DICTATION',
    'abrir diente': 'OPEN_TOOTH_SELECT',
    'abrir cliente': 'OPEN_TOOTH_SELECT',

    // 'guardar diente':'SAVE_TOOTH_SELECT',
    // 'guarda diente':'SAVE_TOOTH_SELECT',
    // 'guardas diente':'SAVE_TOOTH_SELECT',
    // 'garda diente':'SAVE_TOOTH_SELECT',


    'seleccionar diente': 'SELECT_TOOTH',
     'seleccionar cliente': 'SELECT_TOOTH',

    'parar': 'STOP_DICTATION',
    'detener': 'STOP_DICTATION',
    'terminar': 'STOP_DICTATION',
    'parar dictado': 'STOP_DICTATION',
    'detener dictado': 'STOP_DICTATION',
    'terminar dictado': 'STOP_DICTATION',
    'borrar palabra': 'DELETE_WORD',
    'eliminar palabra': 'DELETE_WORD',
    'borrar todo': 'DELETE_ALL',
    'limpiar': 'DELETE_ALL',

    'abrir mayúscula': 'UPPERCASE_ON',
    'abrir mayuscula': 'UPPERCASE_ON',
    'abrir mayúsculas': 'UPPERCASE_ON',
    'abrir mayusculas': 'UPPERCASE_ON',
    'cerrar mayúscula': 'UPPERCASE_OFF',
    'cerrar mayuscula': 'UPPERCASE_OFF',
    'cerrar mayúsculas': 'UPPERCASE_OFF',
    'cerrar mayusculas': 'UPPERCASE_OFF',
    'salto de línea': 'NEW_LINE',
    'salto de llnea': 'NEW_LINE',

    'click vestibular':'SELECT_CHECK_V',
    'clic vestibular':'SELECT_CHECK_V',
    'cli vestibular':'SELECT_CHECK_V',

    'click medial':'SELECT_CHECK_M',
    'clic medial':'SELECT_CHECK_M',
    'cli medial':'SELECT_CHECK_M',

    'click distal':'SELECT_CHECK_D',
    'clic distal':'SELECT_CHECK_D',
    'cli distal':'SELECT_CHECK_D',

    'click palatino':'SELECT_CHECK_P',
    'clic palatino':'SELECT_CHECK_P',
    'cli palatino':'SELECT_CHECK_P',

    'click center':'SELECT_CHECK_C',
    'clic center':'SELECT_CHECK_C',
    'cli center':'SELECT_CHECK_C',

    'click cambiar color':'SELECT_COLOR',
    'clic cambiar color':'SELECT_COLOR',
    'cli cambiar color':'SELECT_COLOR',
    'cambiar color':'SELECT_COLOR',
  };

  // Mapa de comandos de puntuación
  private punctuationCommands: { [key: string]: string } = {
    'punto': '.',
    'coma': ',',
    'punto y coma': ';',
    'dos puntos': ':',
    'signo de pregunta': '?',
    'signo de exclamación': '!',
    'paréntesis abierto': '(',
    'paréntesis cerrado': ')',
    'comillas': '"',
   // 'nueva línea': '\n',
    'salto de línea': '\n',
    'enter': '\n',
    'espacio': ' ',
    'nueva frase': '. ',
    'nuevo párrafo': '\n\n'
  };

  // Comandos de navegación (placeholder - definir según tu app)
  private navigationCommands: { [key: string]: string } = {
    'ir a detalle': 'observations',
    'ir a tratamiento': 'treatment',
    'ir a observaciones': 'observations'
  };

  constructor(private ngZone: NgZone, private _toast:ToastrService) {
    this.initializeSpeechRecognition();
    this.setupFocusListeners();
     this.setupNetworkListeners(); //  Configurar listeners de red
  }

  // === INICIALIZACIÓN ===
//   private initializeSpeechRecognition(): void {
//     console.log("Entra a initializeSpeechRecognition");
//     if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
//       const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
//       this.recognition = new SpeechRecognition();
//       this.isSupported = true;
//       this.recognition.continuous = true;
//       this.recognition.interimResults = true;
//       this.recognition.lang = 'es-ES';

//       this.recognition.onstart = () => this.ngZone.run(() => this.updateState({ isListening: true }));
//       this.recognition.onend = () => this.ngZone.run(() => this.updateState({ isListening: false }));

//      // this.recognition.onerror = (e: any) => this.ngZone.run(() => this.updateState({ error: this.getErrorMessage(e.error) }));
//      // MODIFICADO: Manejo mejorado de errores con detección de red
//       this.recognition.onerror = (e: any) => this.ngZone.run(() => {
//         console.log('🔴 Error de reconocimiento:', e.error);

//         // if (e.error === 'network') {
//         //   this.handleNetworkError();
//         // } else
//         // {


//         //   this.updateState({ error: this.getErrorMessage(e.error) });
//         // }
//         if (e.error === 'network') {
//   this.handleNetworkError();
// } else {
//   if (e.error === 'no-speech') {
//     // Esperar 20 segundos antes de actualizar el estado para el error 'no-speech'
//     // setTimeout(() => {
//     //   this.updateState({ error: this.getErrorMessage(e.error) });
//     //   this._toast.warning("Silencio detectado", "Click en Restaurar");
//     // }, 50000); // 20 segundos
//   this.updateState({ error: this.getErrorMessage(e.error) });
//   this._toast.warning("Silencio detectado", "Reiniciando...");
//   this.startListening(); // volver a iniciar inmediatamente

//   } else {
//     // Para otros errores, actualizar inmediatamente
//     this.updateState({ error: this.getErrorMessage(e.error) });
//   }
// }
//       });
//       //END Modificado
//       this.recognition.onresult = (event: any) => this.ngZone.run(() => this.processVoiceResult(event));
//     } else {
//       this.updateState({ error: 'Reconocimiento de voz no soportado' });
//     }
//   }

private initializeSpeechRecognition(): void {
  console.log("Entra a initializeSpeechRecognition");
  if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
    const SpeechRecognition =
      (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;

    this.recognition = new SpeechRecognition();
    this.isSupported = true;
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'es-ES';

    // Cuando empieza a escuchar
    this.recognition.onstart = () =>
      this.ngZone.run(() => this.updateState({ isListening: true }));

    // Cuando termina (por silencio o cierre normal)
    this.recognition.onend = () =>
      this.ngZone.run(() => {
        console.log("🟡 Reconocimiento finalizado (onend)");
        this.updateState({ isListening: false });

        // Reinicia automáticamente si el dictado sigue activo
        if (this.stateSubject.value.isActive) {
          this._toast.warning("Silencio detectado", "Reiniciando...");
          this.startListening();
        }
      });

    // Manejo de errores
    this.recognition.onerror = (e: any) =>
      this.ngZone.run(() => {
        console.log('🔴 Error de reconocimiento:', e.error);

        if (e.error === 'network') {
          this.handleNetworkError();
        } else if (e.error === 'no-speech') {
          // Algunos navegadores lo lanzan, otros no
          this.updateState({ error: this.getErrorMessage(e.error) });
         // this._toast.warning("Silencio detectado", "Reiniciando...");
          this.startListening();
        } else {
          // Otros errores
          this.updateState({ error: this.getErrorMessage(e.error) });
        }
      });

    // Resultados de voz
    this.recognition.onresult = (event: any) =>
      this.ngZone.run(() => this.processVoiceResult(event));

  } else {
    this.updateState({ error: 'Reconocimiento de voz no soportado' });
  }
}


  private setupFocusListeners(): void {
    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        this.activeElement = target as HTMLInputElement | HTMLTextAreaElement;
        this.updateState({ activeElement: target });
      }
    });
  }

  // === PROCESAMIENTO DE VOZ ===
  private processVoiceResult(event: any): void {
    let finalTranscript = '';
    let interimTranscript = '';

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result.isFinal) {
        finalTranscript += result[0].transcript;
      } else {
        interimTranscript += result[0].transcript;
      }
    }

    if (finalTranscript) {
      this.processCommand(finalTranscript.toLowerCase().trim());
    }
  }

private processCommand(transcript: string): void {
  console.log("=== PROCESANDO COMANDO ===");
  console.log('Comando recibido:', transcript);
  console.log('Estado dictado:', this.stateSubject.value.isDictating);

  this.updateState({ processingCommand: true });

  try {
    //  Detectar si el transcript contiene comando con prefijo
    const commandResult = this.detectAndSeparateCommand(transcript);

    if (commandResult.hasCommand) {
      console.log('🎯 Comando detectado:', commandResult.command);
      console.log('📝 Texto antes del comando:', commandResult.textBefore);

      // Primero escribir el texto antes del comando (si existe)
      if (commandResult.textBefore) {
        this.handleNormalText(commandResult.textBefore);
      }

      // Luego ejecutar el comando
      this.processControlCommand(commandResult.command);
      return;
    }

    // Si no tiene el prefijo "comando", procesar como antes
    // 1. Comandos especiales de dientes (mantener para compatibilidad)
    if (this.handleToothCommands(transcript)) return;

    if(this.handleChekboxhCommands(transcript)) return;

    // 2. Comandos de control exactos (mantener para compatibilidad)
    if (this.handleControlCommands(transcript)) return;

    // 3. Comandos de navegación
    if (this.handleNavigationCommands(transcript)) return;

    // 4. Texto con puntuación al final
    if (this.handleTextWithPunctuation(transcript)) return;

    // 5. Solo comando de puntuación
    if (this.handlePunctuationOnly(transcript)) return;

    // 6. Texto normal de dictado
    if (this.handleNormalText(transcript)) return;

    // 7. Comando no reconocido
    this.handleUnrecognizedCommand(transcript);

  } catch (error) {
    console.error('Error procesando comando:', error);
    this.updateState({
      lastCommand: `Error procesando: "${transcript}"`,
      error: 'Error interno del reconocimiento'
    });
  } finally {
    this.finishCommandProcessing();
  }
}

//  Detectar y separar comando del texto
private detectAndSeparateCommand(transcript: string): {
  hasCommand: boolean;
  textBefore: string;
  command: string;
} {
  const commandPrefixes = ['comando', 'command', 'comand']; // Incluir variaciones
  const lowerTranscript = transcript.toLowerCase();

  for (const prefix of commandPrefixes) {
    // Buscar el prefijo seguido de espacio
    const prefixPattern = new RegExp(`\\b${prefix}\\s+(.+)`, 'i');
    const match = lowerTranscript.match(prefixPattern);

    if (match) {
      // Encontrar la posición del prefijo en el texto original
      const prefixIndex = lowerTranscript.indexOf(prefix);

      // Extraer el texto antes del prefijo
      let textBefore = transcript.substring(0, prefixIndex).trim();

      // Extraer el comando después del prefijo (mantener case original)
      const commandStartIndex = prefixIndex + prefix.length;
      const command = transcript.substring(commandStartIndex).trim();

      console.log(`✅ Comando separado:`);
      console.log(`   Texto antes: "${textBefore}"`);
      console.log(`   Comando: "${command}"`);

      return {
        hasCommand: true,
        textBefore: textBefore || '', // Asegurar que no sea null
        command: command
      };
    }
  }

  return {
    hasCommand: false,
    textBefore: '',
    command: ''
  };
}

// ACTUALIZADO: Método mejorado para detectar prefijo simple (mantener para compatibilidad)
private detectCommandPrefix(transcript: string): { isCommand: boolean; cleanTranscript: string } {
  const commandPrefixes = ['comando', 'command', 'comand'];

  const lowerTranscript = transcript.toLowerCase().trim();

  for (const prefix of commandPrefixes) {
    if (lowerTranscript.startsWith(prefix + ' ')) {
      const cleanTranscript = transcript.substring(prefix.length).trim();
      console.log(`✅ Prefijo "${prefix}" detectado. Comando extraído:`, cleanTranscript);

      return {
        isCommand: true,
        cleanTranscript: cleanTranscript
      };
    }
  }

  return {
    isCommand: false,
    cleanTranscript: transcript
  };
}

//  Procesar comando de control con prefijo
private processControlCommand(cleanTranscript: string): void {
  console.log('🎯 Procesando comando de control:', cleanTranscript);

  try {
    // 1. Comandos especiales de dientes
    if (this.handleToothCommands(cleanTranscript)) {
      console.log('✅ Comando de diente ejecutado');
      return;
    }

    // 2. Comandos de control exactos
    if (this.handleControlCommands(cleanTranscript)) {
      console.log('✅ Comando de control ejecutado');
      return;
    }

    // 3. Comandos de navegación
    if (this.handleNavigationCommands(cleanTranscript)) {
      console.log('✅ Comando de navegación ejecutado');
      return;
    }

    // 4. Solo comando de puntuación (permitir comandos de puntuación con prefijo)
    if (this.handlePunctuationOnly(cleanTranscript)) {
      console.log('✅ Comando de puntuación ejecutado');
      return;
    }

    // 5. Si no coincide con ningún comando conocido
    console.log('❌ Comando no reconocido después del prefijo');
    this.updateState({
      lastCommand: `⚠️ Comando no reconocido: "${cleanTranscript}"`
    });

  } catch (error) {
    console.error('Error procesando comando de control:', error);
    this.updateState({
      lastCommand: `❌ Error en comando: "${cleanTranscript}"`,
      error: 'Error procesando comando de control'
    });
  }
}

// OPCIONAL: Método para agregar más flexibilidad en el futuro
private getAvailableCommands(): string[] {
  const commands = [
    ...Object.keys(this.controlCommands),
    ...Object.keys(this.punctuationCommands),
    ...Object.keys(this.navigationCommands),
    'seleccionar diente [número]',
    'seleccionar cliente [número]'
  ];

  return commands.sort();
}

// OPCIONAL: Método público para obtener ayuda de comandos
public getCommandHelp(): string {
  const commands = this.getAvailableCommands();
  const helpText = commands.map(cmd => `• ${cmd}`).join('\n');

  return `Comandos disponibles (usar con prefijo "Comando"):\n${helpText}`;
}

  // === MANEJADORES ESPECÍFICOS ===

  // 1. Comandos de dientes
  private handleToothCommands(transcript: string): boolean {
    console.log("Entra a handleToothCommands");
    const matchTooth = transcript.match(/seleccionar diente (\d+)/);
    const matchTooth2=transcript.match(/seleccionar cliente (\d+)/);

    if (matchTooth || matchTooth2)
    {
      if(matchTooth)
      {
      const toothNumber = parseInt(matchTooth[1], 10);
      this.uiCommandSubject.next({ type: 'select-tooth', payload: toothNumber });
      this.updateState({ lastCommand: `🦷 Seleccionando diente ${toothNumber}` });
      return true;

      }

      if(matchTooth2)
      {
      const toothNumber = parseInt(matchTooth2[1], 10);
      this.uiCommandSubject.next({ type: 'select-tooth', payload: toothNumber });
      this.updateState({ lastCommand: `🦷 Seleccionando diente ${toothNumber}` });
      return true;

      }


    }
    return false;
  }

  private handleChekboxhCommands(transcript: string): boolean {
    console.log("Entra a handleCheckboxCommands");
    console.log("En handleCheckbox transcript es:", transcript);
    const matchTooth = transcript.match('click vestibular');
    const matchTooth2=transcript.match(/seleccionar cliente (\d+)/);

    if (matchTooth || matchTooth2)
    {
      if(matchTooth)
      {
      const toothNumber = parseInt(matchTooth[1], 10);
      this.uiCommandSubject.next({ type: 'select-check-v'});
      this.updateState({ lastCommand: `🦷 Seleccionando check vestibular` });
      return true;

      }

      if(matchTooth2)
      {
      const toothNumber = parseInt(matchTooth2[1], 10);
      this.uiCommandSubject.next({ type: 'select-tooth', payload: toothNumber });
      this.updateState({ lastCommand: `🦷 Seleccionando diente ${toothNumber}` });
      return true;

      }


    }

    return false;
  }

  //
  // 2. Comandos de control
  private handleControlCommands(transcript: string): boolean {
    const action = this.controlCommands[transcript];
    if (action) {
      console.log('Comando de control:', transcript, '→', action);
      this.executeControlAction(action);
      return true;
    }
    return false;
  }

  // 3. Comandos de navegación
  // private handleNavigationCommands(transcript: string): boolean {
  //   for (const [command, field] of Object.entries(this.navigationCommands)) {
  //     if (transcript.includes(command)) {
  //       console.log('Navegación:', command, '→', field);
  //       this.executeNavigationCommand(field);
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  // 4. Texto con puntuación
  private handleTextWithPunctuation(transcript: string): boolean {
    const result = this.extractTextWithPunctuation(transcript);
    if (result) {
      console.log('Texto + puntuación:', result);
      this.writeTextWithPunctuation(result.text, result.punctuation);
      return true;
    }
    return false;
  }

  // 5. Solo puntuación
  private handlePunctuationOnly(transcript: string): boolean {
    const punctuation = this.punctuationCommands[transcript];
    if (punctuation) {
      console.log('Solo puntuación:', transcript, '→', punctuation);
      this.writePunctuationOnly(punctuation);
      return true;
    }
    return false;
  }

  private handleNormalText(transcript: string): boolean {
    if (this.stateSubject.value.isDictating && this.activeElement) {
      console.log('Texto normal112:', transcript);
      const processedText = this.applyCapitalizationRules(transcript);
      this.appendTextToActiveElement(processedText);
      this.updateState({ lastCommand: `Dictado: "${processedText}-siga dictando"`, });
      return true;
    }
    return false;
  }

  // 7. Comando no reconocido
  // private handleUnrecognizedCommand(transcript: string): void {
  //   console.log('Comando no reconocido:', transcript);
  //   this.updateState({ lastCommand: `Comando no reconocido: "${transcript}"` });
  // }

private handleUnrecognizedCommand(transcript: string): void {
  console.log('Comando no reconocido:', transcript);

  // Capturar el estado actual antes de actualizar
  const currentState = this.stateSubject.value;
  const wasListening = currentState.isListening;
  const wasActive = currentState.isActive;
  const wasDictating = currentState.isDictating;

  // Actualizar el estado con el comando no reconocido
  this.updateState({
    lastCommand: `⚠️ Comando no reconocido: "${transcript}"`
  });

  console.log('Estado antes del comando no reconocido:', {
    isListening: wasListening,
    isActive: wasActive,
    isDictating: wasDictating
  });

  // Si el servicio estaba activo, continuar permitiendo nuevos comandos
  if (wasActive) {
    setTimeout(() => {
      console.log('🔄 Continuando después de comando no reconocido...');

      // Mantener el estado activo y de dictado si estaba activo
      this.updateState({
        isActive: true,
        isDictating: wasDictating, // Preservar el estado de dictado
        lastCommand: `⚠️ Comando no reconocido: "${transcript}" - Puede continuar`
      });

      // Si no estaba escuchando, reiniciar la escucha
      if (!wasListening && wasActive) {
        console.log('🎤 Reiniciando escucha después de comando no reconocido');
        this.startListening();
      }

    }, 1500); // Dar tiempo para que el usuario vea el mensaje de error
  }
}


  // === MÉTODOS DE EXTRACCIÓN Y ANÁLISIS ===

  private extractTextWithPunctuation(transcript: string): { text: string; punctuation: string } | null {
    // Buscar comandos de puntuación al final
    for (const [command, punctuation] of Object.entries(this.punctuationCommands)) {
      const regex = new RegExp(`\\s+${this.escapeRegex(command)}$`, 'i');
      if (regex.test(transcript)) {
        const text = transcript.replace(regex, '').trim();
        if (text.length > 0) {
          return { text, punctuation };
        }
      }
    }
    return null;
  }

  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }


  private writeTextWithPunctuation(text: string, punctuation: string): void
  {
    console.log("Entra a writeTextWithPuntuatio");
    if (!this.ensureActiveElement()) return;

    const processedText = this.applyCapitalizationRules(text);
    const fullText = processedText + punctuation;
    this.appendTextToActiveElement(fullText);
    this.updateState({
      lastCommand: `"${fullText}" agregado`,
      isDictating: true
    });


  }


  private writePunctuationOnly(punctuation: string): void {
    if (!this.ensureActiveElement()) return;

    this.appendTextToActiveElement(punctuation);
    this.ensureDictationContinues(punctuation);
  }

  private ensureActiveElement(): boolean {
    if (!this.activeElement) {
      console.warn('No hay elemento activo');
      this.updateState({ lastCommand: 'No hay campo activo para escribir' });
      return false;
    }
    return true;
  }

  private ensureDictationContinues(punctuation: string): void {
    if (!this.stateSubject.value.isDictating) {
      console.log('Reactivando dictado después de puntuación');
      setTimeout(() => {
        this.startDictation();
        this.updateState({
          lastCommand: `"${punctuation}" + dictado reactivado`
        });
      }, 100);
    } else {
      this.updateState({
        lastCommand: `"${punctuation}" agregado`,
        isDictating: true
      });
    }
  }

  // === EJECUCIÓN DE ACCIONES ===

  private executeControlAction(action: string): void {
    switch (action) {
      // case 'SAVE_TOOTH_SELECT':
      //   this.uiCommandSubject.next({ type: 'save-tooth-select' });
      //   this.updateState({ lastCommand: '🦷 Guarda detalles de diente' });
      //   break;
      case 'OPEN_TOOTH_SELECT':
        this.uiCommandSubject.next({ type: 'open-tooth-select' });
        this.updateState({ lastCommand: '🦷 Abriendo selector de dientes' });
        break;
      case 'SELECT_CHECK_V':
        console.log('Entra en executeControlAction a SELECT_CHECK_V');
        this.uiCommandSubject.next({ type: 'select-check-v' });
        this.updateState({ lastCommand: 'seleccionado cb vestibular' });
        break;

      case 'SELECT_CHECK_M':
        console.log('Entra en executeControlAction a SELECT_CHECK_M');
        this.uiCommandSubject.next({ type: 'select-check-m' });
        this.updateState({ lastCommand: 'seleccionado cb medial' });
        break;

      case 'SELECT_CHECK_D':
        console.log('Entra en executeControlAction a SELECT_CHECK_D');
        this.uiCommandSubject.next({ type: 'select-check-d' });
        this.updateState({ lastCommand: 'seleccionado cb distal' });
        break;

      case 'SELECT_CHECK_P':
        console.log('Entra en executeControlAction a SELECT_CHECK_P');
        this.uiCommandSubject.next({ type: 'select-check-p' });
        this.updateState({ lastCommand: 'seleccionado cb palatino' });
        break;

      case 'SELECT_CHECK_C':
        console.log('Entra en executeControlAction a SELECT_CHECK_C');
        this.uiCommandSubject.next({ type: 'select-check-c' });
        this.updateState({ lastCommand: 'seleccionado cb center' });
        break;




      case 'SELECT_COLOR':
        this.uiCommandSubject.next({ type: 'select-color' });
        this.updateState({ lastCommand: 'cambiando color' });
        break;
      case 'START_DICTATION':
        this.startDictation();
        break;
      case 'STOP_DICTATION':
        this.stopDictation();
        break;
      case 'DELETE_WORD':
        this.deleteLastWord();
        break;
      case 'DELETE_ALL':
        this.clearActiveElement();
        break;

      case 'UPPERCASE_ON':
        this.enableUppercaseMode();
        break;
      case 'UPPERCASE_OFF':
        this.disableUppercaseMode();
        break;
      case 'NEW_LINE':
        this.executeNewLine();
        break;
    }
  }
//NUEVO
//#region recuperar cursor

  // private executeNavigationCommand(field: string): void {
  //   this.commandExecuted$.next({ command: 'NAVIGATE', targetField: field });
  //   this.updateState({ lastCommand: `Navegando a: ${field}` });
  // }
// Reemplazar el método executeNavigationCommand existente con esta versión mejorada:

private executeNavigationCommand(field: string): void {
  // Emitir el comando de navegación
  this.commandExecuted$.next({ command: 'NAVIGATE', targetField: field });
  this.updateState({ lastCommand: `Navegando a: ${field}` });

  // NUEVO: Asegurar que el cursor sea visible después de la navegación
  this.ensureCursorVisibilityAfterNavigation(field);
}

// NUEVO: Método para asegurar visibilidad del cursor después de navegación
private ensureCursorVisibilityAfterNavigation(field: string): void {
  // Dar tiempo para que la navegación se complete
  setTimeout(() => {
    this.focusAndShowCursor(field);
  }, 100);

  // Segundo intento en caso de que el primero falle (común después de fallos de red)
  setTimeout(() => {
    this.focusAndShowCursor(field);
  }, 300);

  // Tercer intento para casos más problemáticos
  setTimeout(() => {
    this.focusAndShowCursor(field);
  }, 600);
}

// NUEVO: Método para enfocar y mostrar cursor
private focusAndShowCursor(field: string): void {

  console.log(`🎯 Intentando enfocar campo: ${field}`);

  // Estrategia 1: Buscar por atributo data-voice-field
  let targetElement = document.querySelector(`[data-voice-field="${field}"]`) as HTMLInputElement | HTMLTextAreaElement;

  // Estrategia 2: Buscar por ID
  if (!targetElement) {
    targetElement = document.getElementById(field) as HTMLInputElement | HTMLTextAreaElement;
  }

  // Estrategia 3: Buscar por name
  if (!targetElement) {
    targetElement = document.querySelector(`[name="${field}"]`) as HTMLInputElement | HTMLTextAreaElement;
  }

  // Estrategia 4: Buscar por clase CSS
  if (!targetElement) {
    targetElement = document.querySelector(`.field-${field}`) as HTMLInputElement | HTMLTextAreaElement;
  }

  if (targetElement)
  {
    console.log("En focusAndShowCursor, targeElement es:", targetElement);
    this.applyFocusAndCursor(targetElement, field);
  } else {
    console.warn(`❌ No se encontró el campo: ${field}`);
    this.updateState({
      lastCommand: `⚠️ Campo "${field}" no encontrado`,
      error: `No se pudo localizar el campo: ${field}`
    });
  }
}

// NUEVO: Aplicar foco y cursor de manera robusta
private applyFocusAndCursor(element: HTMLInputElement | HTMLTextAreaElement, field: string): void {
  try {
    console.log(`✅ Campo encontrado: ${field}, aplicando foco y cursor`);

    // 1. Scroll al elemento para asegurar visibilidad
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });

    // 2. Remover cualquier foco existente
    if (document.activeElement && document.activeElement !== element) {
      (document.activeElement as HTMLElement).blur();
    }

    // 3. Aplicar foco múltiples veces para asegurar efectividad
    element.focus();

    // 4. Forzar foco con click simulado (útil después de fallos de red)
    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(clickEvent);

    // 5. Asegurar que el cursor esté al final del contenido
    const textLength = element.value.length;
    if (typeof element.setSelectionRange === 'function') {
      element.setSelectionRange(textLength, textLength);
    }

    // 6. Foco adicional después de establecer la selección
    element.focus();

    // 7. Activar el elemento como activeElement del servicio
    this.activeElement = element;
    this.updateState({
      activeElement: element,
      currentField: field,
      lastCommand: `✅ Navegado a "${field}" - cursor visible`,
      error: null
    });

    // 8. Emitir evento adicional para notificar a la UI
    this.uiCommandSubject.next({
      type: 'navigation-completed',
      payload: { field: field, element: element }
    });

    console.log(`🎯 Foco aplicado exitosamente a: ${field}`);

  } catch (error) {
    console.error(`❌ Error aplicando foco a ${field}:`, error);
    this.updateState({
      lastCommand: `❌ Error navegando a "${field}"`,
      error: `Error enfocando campo: ${field}`
    });
  }
}

// NUEVO: Método público para navegación manual con cursor visible (útil para recuperación)
public navigateToFieldWithCursor(field: string): void {
  console.log(`🎯 Navegación manual solicitada a: ${field}`);
  this.executeNavigationCommand(field);
}

// NUEVO: Método para verificar si un campo está correctamente enfocado
public isFieldProperlyFocused(field: string): boolean {
  const targetElement = document.querySelector(`[data-voice-field="${field}"]`) ||
                       document.getElementById(field) ||
                       document.querySelector(`[name="${field}"]`);

  if (!targetElement) return false;

  const isActiveElement = document.activeElement === targetElement;
  const hasFocus = targetElement === this.activeElement;

  console.log(`Verificación de foco para ${field}:`, {
    found: !!targetElement,
    isActiveElement,
    hasFocus,
    properly_focused: isActiveElement && hasFocus
  });

  return isActiveElement && hasFocus;
}

// NUEVO: Método para recuperar foco después de fallos de red
public recoverFieldFocus(): void {
  const currentState = this.stateSubject.value;

  if (currentState.currentField) {
    console.log(`🔄 Recuperando foco en campo: ${currentState.currentField}`);
    this.ensureCursorVisibilityAfterNavigation(currentState.currentField);
  } else if (this.activeElement) {
    console.log('🔄 Recuperando foco en elemento activo');
    this.applyFocusAndCursor(this.activeElement, 'campo-activo');
  } else {
    console.log('⚠️ No hay campo para recuperar foco');
  }
}



  //#endregion recuperar cursor


  // === MÉTODOS DE DICTADO ===

  private startDictation(): void {
    if (!this.activeElement) {
      this.updateState({ lastCommand: 'Selecciona un campo para dictar' });
      return;
    }

    this.updateState({
      isDictating: true,
      lastCommand: '🎤 Dictado iniciado'
    });
  }

  private stopDictation(): void {
    this.updateState({
      isDictating: false,
      lastCommand: '⏹️ Dictado detenido'
    });
  }


private deleteLastWord(): void {
  if (!this.activeElement) return;

  const currentValue = this.activeElement.value;

  // Si el campo está vacío, no hay nada que borrar
  if (!currentValue || currentValue.length === 0) {
    this.updateState({ lastCommand: '🗑️ No hay texto para borrar' });
    return;
  }

  // Encontrar la última palabra usando regex que respeta el formato
  // Esta regex busca la última secuencia de caracteres no-espacios al final del texto
  const lastWordPattern = /\s*\S+\s*$/;
  const match = currentValue.match(lastWordPattern);

  if (match) {
    // Calcular la posición donde empieza la última palabra
    const matchStart = currentValue.lastIndexOf(match[0]);

    // Extraer el texto hasta antes de la última palabra
    let newValue = currentValue.substring(0, matchStart);

    // Si después de borrar la palabra queda un espacio al final, lo conservamos
    // pero eliminamos espacios excesivos
    newValue = newValue.replace(/\s+$/, ''); // Eliminar espacios finales

    this.activeElement.value = newValue;

    // Trigger change event
    const event = new Event('input', { bubbles: true });
    this.activeElement.dispatchEvent(event);

    this.updateState({ lastCommand: '🗑️ Última palabra eliminada (formato preservado)' });

    console.log('Palabra eliminada. Texto resultante:', newValue);
  } else {
    // Si no encuentra ninguna palabra (texto solo con espacios/saltos)
    this.updateState({ lastCommand: '🗑️ No hay palabras para eliminar' });
  }
}


  private clearActiveElement(): void {
    if (!this.activeElement) return;

    this.activeElement.value = '';
    this.updateState({ lastCommand: '🗑️ Campo limpiado' });
  }

 private appendTextToActiveElement(text: string): void
 {
  console.log("Entra a appendTextToActiveElement116");
    if (!this.activeElement) return;

    const currentValue = this.activeElement.value;
    // Agregar espacio solo si hay contenido previo y el texto no empieza con puntuación
    const needsSpace = currentValue && !this.isPunctuation(text.charAt(0));
    const newValue = currentValue + (needsSpace ? ' ' : '') + text;
    this.activeElement.value = newValue;



    this.updateState({
      isDictating:true,
      isActive:true,
      lastCommand:"dictado realizado"
    });
    // console.log("Despúes de 1011, this.updateState es:", this.updateState);
   //this.startMonitoring();


   // Trigger change event
    const event = new Event('input', { bubbles: true });
    this.activeElement.dispatchEvent(event);
  }

  // Verifica si un carácter es puntuación
  private isPunctuation(char: string): boolean {
    const punctuationChars = '.,;:!?()[]{}"\'-';
    return punctuationChars.includes(char);
  }

  // === MÉTODOS AUXILIARES ===

  public updateState(newState: Partial<VoiceWritingState>): void {
    const currentState = this.stateSubject.value;
    //this.testUpdate=currentState.isDictating;
    console.log("currentState es:", this.stateSubject.value);
   // console.log("this.testUpdate es:", this.testUpdate);
    this.stateSubject.next({ ...currentState, ...newState });
  }

    public updateStateDictating(newState: Partial<VoiceWritingState>): void {
    const currentState = this.stateSubject.value;
     console.log("currentState.isDictating es:", currentState.isDictating);
    this.stateSubject.next({ ...currentState, ...newState });
  }


  private finishCommandProcessing(): void {
    if (this.commandProcessingTimeout) {
      clearTimeout(this.commandProcessingTimeout);
    }

    this.commandProcessingTimeout = setTimeout(() => {
      this.updateState({ processingCommand: false });
    }, 500);
  }

// MODIFICADO: Método getErrorMessage con mejor manejo de red
  private getErrorMessage(error: string): string {
    const errorMessages: { [key: string]: string } = {
      'no-speech': 'No se detectó voz',
      'audio-capture': 'Error de captura de audio',
      'not-allowed': 'Micrófono no permitido',
      'network': 'Error de red - verificando conexión...', // Mensaje temporal
      'aborted': 'Reconocimiento abortado'
    };
    return errorMessages[error] || `Error: ${error}`;
  }

  // === MÉTODOS PÚBLICOS ===

  public start(): void {
    if (!this.isSupported) {
      this.updateState({ error: 'Reconocimiento de voz no soportado' });
      return;
    }

    if (this.recognition && !this.stateSubject.value.isListening) {
      try {
        this.recognition.start();
        this.updateState({ isActive: true, error: null });
      } catch (error) {
        console.error('Error iniciando reconocimiento:', error);
        this.updateState({ error: 'Error iniciando reconocimiento de voz' });
      }
    }
  }

  public stop(): void {
    if (this.recognition && this.stateSubject.value.isListening) {
      this.recognition.stop();
      this.updateState({
        isActive: false,
        isDictating: false,
        lastCommand: 'Reconocimiento detenido'
      });
    }
  }

  public isListening(): boolean {
    return this.stateSubject.value.isListening;
  }

  public getCurrentState(): VoiceWritingState {
    return this.stateSubject.value;
  }

  public startVoiceWriting(): void
    {
      console.log("Entra a starVoiceWriting");
    if (!this.isSupported)
      {
        this.updateState({
          error: 'Reconocimiento de voz no soportado'
        });
        return;
      }

      this.restartAttempts = 0; // Reset reintentos

      this.updateState({
        isActive: true,
        error: null,
        lastCommand: '🎤 Escritura por voz iniciada. Posicione el cursor en un campo y diga "texto"'
      });

      this.startListening();
  }

  public stopVoiceWriting(): void {

    this.updateState({
      isActive: false,
      isDictating: false,
      lastCommand: '⏹️ Escritura por voz detenida'
    });

    this.stopListening();

    // Limpiar timeouts
    if (this.recognitionTimeout) {
      clearTimeout(this.recognitionTimeout);
    }
    if (this.commandProcessingTimeout) {
      clearTimeout(this.commandProcessingTimeout);
    }
  }

    private startListening(): void {
    console.log("Entra a startListening112");
    if (this.isSupported && this.recognition && !this.stateSubject.value.isListening) {
      try {
        this.recognition.start();
      } catch (error) {
        console.error('❌ Error al iniciar reconocimiento:', error);
        this.updateState({
          error: 'Error al iniciar el reconocimiento de voz'
        });
      }
    }
  }

  private stopListening(): void {
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.error('❌ Error al detener reconocimiento:', error);
      }
    }
  }

  public getActiveElement(): HTMLInputElement | HTMLTextAreaElement | null {
    return this.activeElement;
  }

    public getDebugInfo(): any {
    return {
      isSupported: this.isSupported,
      recognition: !!this.recognition,
      activeElement: this.activeElement?.tagName,
      state: this.stateSubject.value,
      restartAttempts: this.restartAttempts
    };
  }
    public isVoiceWritingSupported(): boolean { return this.isSupported; }


private applyCapitalizationRules(text: string): string {
    if (!this.activeElement || !text) return text;

    const currentValue = this.activeElement.value;
    const currentState = this.stateSubject.value;

    // Si el modo mayúsculas está activo, convertir todo a mayúsculas
    if (currentState.uppercaseMode) {
      console.log('Modo mayúsculas activo - convirtiendo texto:', text);
      return text.toUpperCase();
    }

    // Si no está en modo mayúsculas, aplicar reglas normales de capitalización
    const shouldCapitalize = this.shouldCapitalizeNextWord(currentValue);

    if (shouldCapitalize) {
      console.log('Aplicando mayúscula inicial a:', text);
      return this.capitalizeFirstLetter(text);
    }

    return text;
  }

  // Verifica si la siguiente palabra debe comenzar con mayúscula
  private shouldCapitalizeNextWord(currentText: string): boolean {
    if (!currentText || currentText.length === 0) {
      // Si el campo está vacío, capitalizar la primera palabra
      return true;
    }

    // Buscar el último punto en el texto
    const lastDotIndex = currentText.lastIndexOf('.');

    if (lastDotIndex === -1) {
      // No hay puntos, no capitalizar (a menos que sea el inicio)
      return currentText.trim().length === 0;
    }

    // Verificar si después del último punto solo hay espacios o está al final
    const textAfterLastDot = currentText.substring(lastDotIndex + 1);
    const hasOnlySpacesAfterDot = /^\s*$/.test(textAfterLastDot);

    if (hasOnlySpacesAfterDot) {
      console.log('Detectado punto seguido de espacios. Siguiente palabra será capitalizada.');
      return true;
    }

    return false;
  }

  // Capitaliza la primera letra de un texto
  private capitalizeFirstLetter(text: string): string {
    if (!text || text.length === 0) return text;

    // Buscar la primera letra (ignorar espacios iniciales)
    const trimmedText = text.trimLeft();
    if (trimmedText.length === 0) return text;

    const firstLetter = trimmedText.charAt(0).toUpperCase();
    const restOfText = trimmedText.substring(1);
    const leadingSpaces = text.substring(0, text.length - trimmedText.length);

    return leadingSpaces + firstLetter + restOfText;
  }

  private enableUppercaseMode(): void {
    this.updateState({
      uppercaseMode: true,
      lastCommand: '🔤 Modo mayúsculas ACTIVADO - siguiente texto en mayúsculas'
    });
    console.log('Modo mayúsculas activado');
  }

  private disableUppercaseMode(): void {
    this.updateState({
      uppercaseMode: false,
      lastCommand: '🔡 Modo mayúsculas DESACTIVADO - texto volverá a minúsculas'
    });
    console.log('Modo mayúsculas desactivado');

    // Agregar espacio automáticamente al cerrar mayúsculas
    if (this.activeElement && this.activeElement.value.length > 0) {
      const currentValue = this.activeElement.value;
      // Solo agregar espacio si el último carácter no es ya un espacio
      if (!currentValue.endsWith(' ')) {
        this.activeElement.value = currentValue + ' ';
        console.log('Espacio agregado automáticamente al cerrar mayúsculas');

        // Trigger change event
        const event = new Event('input', { bubbles: true });
        this.activeElement.dispatchEvent(event);
      }
    }
  }

  public isUppercaseMode(): boolean {
    return this.stateSubject.value.uppercaseMode;
  }

private executeNewLine(): void {
  if (!this.ensureActiveElement()) return;

  console.log('Ejecutando comando nueva línea: punto + salto de línea');

  const currentValue = this.activeElement.value;
  const trimmedValue = currentValue.trim();
  let newContent = '';

  // Determinar qué agregar basado en el contenido actual
  if (!trimmedValue) {
    // Campo vacío: solo nueva línea
    newContent = '\n';
    console.log('Campo vacío, solo agregando nueva línea');
  } else if (trimmedValue.endsWith('.')) {
    // Ya termina con punto: solo nueva línea
    newContent = '\n';
    console.log('Texto ya termina con punto, solo agregando nueva línea');
  } else {
    // No termina con punto: agregar punto + nueva línea
    newContent = '.\n';
    console.log('Agregando punto y nueva línea');
  }

  // Actualizar el valor asegurando que no hay espacios al inicio de la nueva línea
  this.activeElement.value = currentValue + newContent;

this.activeElement.value = this.activeElement.value.replace(/\n\s+/g, '\n');

//
  // Posicionar cursor al final del texto (después del salto de línea)
  this.setCursorToEnd();

  // Trigger change event
  const event = new Event('input', { bubbles: true });
  this.activeElement.dispatchEvent(event);

  // Mantener dictado activo y actualizar estado
  this.updateState({
    lastCommand: '📝 Nueva línea agregada (punto + salto)',
    isDictating: true
  });

  console.log('Nueva línea ejecutada correctamente');
}

// Método auxiliar para posicionar el cursor al final
private setCursorToEnd(): void {
  if (this.activeElement && typeof this.activeElement.setSelectionRange === 'function') {
    const length = this.activeElement.value.length;
    this.activeElement.setSelectionRange(length, length);
    this.activeElement.focus();
  }
}


private setupNetworkListeners(): void {
    // Listener para eventos online/offline del navegador
    window.addEventListener('online', () => {
      console.log('🌐 Navegador detectó conexión online');
      this.ngZone.run(() => {
        this.handleNetworkOnline();
      });
    });

    window.addEventListener('offline', () => {
      console.log('🌐 Navegador detectó conexión offline');
      this.ngZone.run(() => {
        this.updateState({
          networkStatus: 'offline',
          lastCommand: '📡 Conexión perdida - detectada por navegador'
        });
      });
    });
  }

  // Manejo cuando la red vuelve online
  private handleNetworkOnline(): void {
    console.log('🌐 Red online detectada - iniciando recuperación');
    this.updateState({
      networkStatus: 'online',
      lastCommand: '📡 Conexión restaurada - reiniciando servicios...'
    });

    // Reiniciar el servicio de voz después de un breve delay
    setTimeout(() => {
      this.recoverFromNetworkError();
    }, 1000);
  }

//  Manejo específico de errores de red
  private handleNetworkError(): void {
    console.log('🔴 Error de red detectado - iniciando proceso de recuperación');

    this.updateState({
      error: 'Error de red - verificando conexión...',
      networkStatus: 'offline',
      isRecovering: true,
      lastCommand: '📡 Error de red detectado - escaneando...'
    });

    // Detener el reconocimiento actual
    this.stopListeningInternal();

    // Resetear contadores
    this.currentNetworkRetries = 0;

    // Iniciar el escaneo de red
    this.startNetworkRecovery();
  }

  //  Iniciar proceso de recuperación de red
  private startNetworkRecovery(): void {
    console.log('🔄 Iniciando escaneo de red...');

    // Cancelar cualquier escaneo anterior
    if (this.networkCheckSubscription) {
      this.networkCheckSubscription.unsubscribe();
    }

    this.updateState({
      networkStatus: 'recovering',
      lastCommand: `📡 Escaneando red... intento ${this.currentNetworkRetries + 1}/${this.maxNetworkRetries}`
    });

    // Crear observable que verifica la red cada X segundos
    this.networkCheckSubscription = interval(this.networkCheckInterval)
      .pipe(
        takeWhile(() => this.currentNetworkRetries < this.maxNetworkRetries &&
                       this.stateSubject.value.networkStatus !== 'online'),
        delay(100) // Pequeño delay para evitar spam
      )
      .subscribe(() => {
        this.checkNetworkAndRecover();
      });
  }

  // Verificar red y recuperar si está disponible
  private async checkNetworkAndRecover(): Promise<void> {
    this.currentNetworkRetries++;

    console.log(`🔍 Verificando red - intento ${this.currentNetworkRetries}/${this.maxNetworkRetries}`);

    this.updateState({
      lastCommand: `📡 Verificando conexión... ${this.currentNetworkRetries}/${this.maxNetworkRetries}`
    });

    try {
      // Verificar conectividad real (no solo el estado del navegador)
      const isConnected = await this.testNetworkConnectivity();

      if (isConnected) {
        console.log('✅ Red recuperada - reiniciando servicios');
        this.recoverFromNetworkError();
      } else {
        console.log(`❌ Red aún no disponible (${this.currentNetworkRetries}/${this.maxNetworkRetries})`);

        // Si llegamos al máximo de reintentos
        if (this.currentNetworkRetries >= this.maxNetworkRetries) {
          this.handleNetworkRecoveryFailure();
        }
      }
    } catch (error) {
      console.error('Error verificando red:', error);

      if (this.currentNetworkRetries >= this.maxNetworkRetries) {
        this.handleNetworkRecoveryFailure();
      }
    }
  }

  // Probar conectividad real a internet
  public testNetworkConnectivity(): Promise<boolean> {
    return new Promise((resolve) => {
      // Usar multiple estrategias para verificar conectividad
      const timeoutMs = 2000;
      let resolved = false;

      // Estrategia 1: Verificar navigator.onLine
      if (!navigator.onLine) {
        console.log('📡 Navigator.onLine indica offline');
        resolve(false);
        return;
      }

      // Estrategia 2: Intentar fetch a un recurso confiable
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.log('📡 Timeout en test de conectividad');
          resolve(false);
        }
      }, timeoutMs);

      // Usar varios endpoints para mayor confiabilidad
      const testUrls = [
        'https://www.google.com/favicon.ico',
        'https://httpbin.org/status/200',
        'https://jsonplaceholder.typicode.com/posts/1'
      ];

      // Probar con el primer endpoint disponible
      fetch(testUrls[0], {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      })
      .then(() => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          console.log('✅ Conectividad confirmada');
          resolve(true);
        }
      })
      .catch(() => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          console.log('❌ Sin conectividad');
          resolve(false);
        }
      });
    });
  }

  //NUEVO
// Reemplazar el método recoverFromNetworkError existente con esta versión mejorada:

public recoverFromNetworkError(): void {
  console.log('🎉 Recuperación de red exitosa');

  // Cancelar el escaneo de red
  if (this.networkCheckSubscription) {
    this.networkCheckSubscription.unsubscribe();
    this.networkCheckSubscription = null;
  }

  // Resetear contadores y estado
  this.currentNetworkRetries = 0;
  this.restartAttempts = 0;

  this.updateState({
    error: null,
    networkStatus: 'online',
    isRecovering: false,
    lastCommand: '🎉 ¡Conexión restaurada! Servicio de voz reiniciado'
  });

  // Reinicializar el reconocimiento si estaba activo
  if (this.stateSubject.value.isActive) {
    console.log('🔄 Reiniciando reconocimiento de voz...');

    setTimeout(() => {
      this.startListening();

      // NUEVO: Recuperar el foco del cursor después de la recuperación de red
      this.recoverFieldFocus();

      this.updateState({
        lastCommand: '✅ Servicio de voz completamente restaurado'
      });
    }, 500);
  }

  // Notificar a la UI sobre la recuperación
  this.uiCommandSubject.next({
    type: 'network-recovered',
    payload: { message: 'Conexión restaurada - servicio reiniciado' }
  });
}

// NUEVO: También modificar el método handleNavigationCommands para mejor logging
private handleNavigationCommands(transcript: string): boolean {
  for (const [command, field] of Object.entries(this.navigationCommands)) {
    if (transcript.includes(command)) {
      console.log('🎯 Comando de navegación detectado:', command, '→', field);
      this.executeNavigationCommand(field);
      return true;
    }
  }
  return false;
}
  //End NUEVO

  // Manejo cuando falla la recuperación de red
  private handleNetworkRecoveryFailure(): void {
    console.log('❌ Falló la recuperación de red después de múltiples intentos');

    // Cancelar el escaneo
    if (this.networkCheckSubscription) {
      this.networkCheckSubscription.unsubscribe();
      this.networkCheckSubscription = null;
    }

    this.updateState({
      error: 'Sin conexión a internet. Verifique su red y reinicie manualmente.',
      networkStatus: 'offline',
      isRecovering: false,
      lastCommand: '❌ No se pudo restaurar la conexión. Intervención manual requerida.'
    });

    // Notificar a la UI sobre el fallo
    this.uiCommandSubject.next({
      type: 'network-recovery-failed',
      payload: { message: 'No se pudo restaurar la conexión automáticamente' }
    });
  }

  // Método público para reinicio manual
  public manualNetworkRestart(): void {
    console.log('🔄 Reinicio manual solicitado por usuario');

    // Cancelar cualquier proceso de recuperación en curso
    if (this.networkCheckSubscription) {
      this.networkCheckSubscription.unsubscribe();
      this.networkCheckSubscription = null;
    }

    // Verificar conectividad y reiniciar
    this.testNetworkConnectivity().then(isConnected => {
      if (isConnected) {
        this.recoverFromNetworkError();
      } else {
        this.updateState({
          error: 'Aún sin conexión a internet',
          lastCommand: '❌ Verificación manual: sin conexión'
        });
      }
    });
  }
//  Método auxiliar para detener reconocimiento sin afectar estado
  private stopListeningInternal(): void
  {
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.error('Error deteniendo reconocimiento:', error);
      }
    }


}

//  Método público para verificar si está en recuperación
  public isRecovering(): boolean {
    return this.stateSubject.value.isRecovering;
  }

// === CLEANUP ===
  // Método público para limpieza manual (llamar desde componente)
  public cleanup(): void {
    console.log('🧹 Limpieza manual del servicio');

    // Limpiar suscripciones de red
    if (this.networkCheckSubscription) {
      this.networkCheckSubscription.unsubscribe();
      this.networkCheckSubscription = null;
    }

    // Limpiar timeouts
    if (this.recognitionTimeout) {
      clearTimeout(this.recognitionTimeout);
      this.recognitionTimeout = null;
    }
    if (this.commandProcessingTimeout) {
      clearTimeout(this.commandProcessingTimeout);
      this.commandProcessingTimeout = null;
    }

    // Detener reconocimiento
    this.stopListeningInternal();

    // Resetear estado
    this.updateState({
      isActive: false,
      isDictating: false,
      isRecovering: false,
      networkStatus: 'offline',
      lastCommand: '🧹 Servicio limpiado manualmente'
    });
  }

  //  Método para reinicializar completamente el servicio
  public reinitialize(): void {
    console.log('🔄 Reinicializando servicio completo');

    // Primero limpiar todo
    this.cleanup();

    // Luego reinicializar
    setTimeout(() => {
      this.restartAttempts = 0;
      this.currentNetworkRetries = 0;
      this.initializeSpeechRecognition();

      this.updateState({
        networkStatus: 'online',
        error: null,
        lastCommand: '✅ Servicio reinicializado completamente'
      });

      console.log('✅ Servicio reinicializado');
    }, 500);
  }


   startMonitoring(): void {
    // Reiniciar contador si se llama nuevamente
    this.iterationCount = 0;

    // Limpiar intervalo previo si existe
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      this.iterationCount++;

      console.log("Después de 1012, this.stateSubject.isDictating es:", this.stateSubject.value);
      console.log(`Iteración ${this.iterationCount} de ${this.MAX_ITERATIONS}`);

      // Detener después de 8 iteraciones
      if (this.iterationCount >= this.MAX_ITERATIONS) {
        clearInterval(this.intervalId);
        this.intervalId = null;
        console.log('Monitoreo completado - 8 iteraciones finalizadas');
      }
    }, 10000); // 10 segundos
  }

  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('Monitoreo detenido manualmente');
    }
  }





}
